import { instance } from "../index";

/**
 * Account deletion API functions
 */
const BASE_ENDPOINT = "/api/account-deletion";

/**
 * Request account deletion
 * @param {string} email - User's email address
 * @param {string} deletionReason - Optional reason for deletion
 * @returns {Promise<object>} API response
 */
export const requestAccountDeletion = async (email, deletionReason = "") => {
  try {
    const response = await instance.post(`${BASE_ENDPOINT}/request`, {
      email: email.trim().toLowerCase(),
      deletionReason: deletionReason.trim(),
    });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        success: false,
        message: "حدث خطأ أثناء طلب حذف الحساب",
      }
    );
  }
};

/**
 * Verify deletion token
 * @param {string} token - Deletion token from email
 * @param {string} email - User's email address
 * @returns {Promise<object>} API response
 */
export const verifyDeletionToken = async (token, email) => {
  try {
    const response = await instance.get(`${BASE_ENDPOINT}/verify`, {
      params: {
        token: encodeURIComponent(token),
        email: encodeURIComponent(email),
      },
    });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        success: false,
        message: "رمز حذف الحساب غير صالح أو منتهي الصلاحية",
      }
    );
  }
};

/**
 * Confirm account deletion
 * @param {string} token - Deletion token from email
 * @param {string} email - User's email address
 * @param {string} password - User's current password
 * @param {string} deletionCode - Optional deletion code (alternative to token)
 * @returns {Promise<object>} API response
 */
export const confirmAccountDeletion = async (
  token,
  email,
  password,
  deletionCode = null
) => {
  try {
    const requestBody = {
      token,
      email: email.trim().toLowerCase(),
      password,
    };

    if (deletionCode) {
      requestBody.deletionCode = deletionCode;
    }

    const response = await instance.post(
      `${BASE_ENDPOINT}/confirm`,
      requestBody
    );
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        success: false,
        message: "حدث خطأ أثناء حذف الحساب",
      }
    );
  }
};

/**
 * Get deletion status by ID
 * @param {string} deletionId - Deletion request ID
 * @returns {Promise<object>} API response
 */
export const getDeletionStatus = async (deletionId) => {
  try {
    const response = await instance.get(
      `${BASE_ENDPOINT}/status/${deletionId}`
    );
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        success: false,
        message: "لم يتم العثور على طلب الحذف",
      }
    );
  }
};

/**
 * Cancel deletion request
 * @param {string} deletionId - Deletion request ID
 * @param {string} email - User's email address
 * @returns {Promise<object>} API response
 */
export const cancelDeletionRequest = async (deletionId, email) => {
  try {
    const response = await instance.post(`${BASE_ENDPOINT}/cancel`, {
      deletionId,
      email: email.trim().toLowerCase(),
    });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        success: false,
        message: "حدث خطأ أثناء إلغاء طلب الحذف",
      }
    );
  }
};

/**
 * Test email configuration (for debugging)
 * @returns {Promise<object>} API response
 */
export const testEmailConfiguration = async () => {
  try {
    const response = await instance.get(`${BASE_ENDPOINT}/test-email`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        success: false,
        message: "فشل في اختبار إعدادات البريد الإلكتروني",
      }
    );
  }
};

/**
 * Check service health
 * @returns {Promise<object>} API response
 */
export const checkServiceHealth = async () => {
  try {
    const response = await instance.get(`${BASE_ENDPOINT}/health`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        success: false,
        message: "خدمة حذف الحسابات غير متاحة",
      }
    );
  }
};

/**
 * Utility function to handle API errors consistently
 * @param {Error} error - Error object from API call
 * @returns {string} User-friendly error message in Arabic
 */
export const handleAccountDeletionError = (error) => {
  const commonErrors = {
    400: "بيانات غير صالحة",
    401: "كلمة المرور غير صحيحة",
    404: "المستخدم غير موجود",
    423: "الحساب مؤقتاً مقفل",
    429: "تم تجاوز الحد المسموح من المحاولات",
    500: "خطأ في الخادم",
  };

  // Check if it's an API error response
  if (error.response?.status) {
    return (
      error.response.data?.message ||
      commonErrors[error.response.status] ||
      "حدث خطأ غير متوقع"
    );
  }

  // Check if it's a pre-formatted error from our API functions
  if (error.message) {
    return error.message;
  }

  // Default error message
  return "حدث خطأ أثناء الاتصال بالخادم";
};

/**
 * Show user-friendly notification
 * @param {string} message - Message to display
 * @param {string} type - Notification type (success, error, info, warning)
 */
export const showNotification = (message, type = "info") => {
  // This will be implemented in the components
  // For now, just console log
  console.log(`[${type.toUpperCase()}] ${message}`);
};
